import { useHandler } from "@redotech/react-util/hook";
import { useEffect } from "react";
import {
  useNotificationStream,
  type NotificationEventOfType,
  type NotificationEventType,
} from "./use-notification-stream";

/**
 * Hook for subscribing to notification events.
 *
 * Components can use this to react to specific event types from the
 * notification stream. The subscription is automatically unsubscribed and cleaned up
 * when the component unmounts.
 *
 * @param notificationEventType - The type of event to subscribe to (e.g., 'messageCreated')
 * @param handler - Callback function that receives the event payload
 *
 * @example
 * ```typescript
 * // Sync messages to TanStack DB
 * useOnNotification('messageCreated', (event) => {
 *   collections.messages.utils.writeInsert({
 *     ...event.message,
 *     conversationId: event.conversationId,
 *   });
 * });
 *
 * // Show toast notifications
 * useOnNotification('messageCreated', (event) => {
 *   showToast(`New message from ${event.message.senderName}`);
 * });
 * ```
 */
export function useOnNotification<T extends NotificationEventType>(
  notificationEventType: T,
  handler: (notificationEvent: NotificationEventOfType<T>) => void,
): void {
  const { subscribe } = useNotificationStream();
  const notificationEventHandler = useHandler(handler);

  // useEffect runs the returned `unsubscribe` function on cleanup or unmounting
  // of the subscribing component
  useEffect(() => {
    const unsubscribe = subscribe(
      notificationEventType,
      notificationEventHandler,
    );
    return unsubscribe;
  }, [subscribe, notificationEventType, notificationEventHandler]);
}
